import {
    ArrowLeft,
    ArrowRight,
    Box,
    Close,
    Divider,
    Heading,
    LogoLoader,
    TextBlock,
    ZoomIn,
    ZoomOut,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import style from "components/asset-management-system/asset-side-panel.scss";
import { GradeBadge } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { GradingActionSelect } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grading-action-select/GradingActionSelect";
import {
    assetGradingService,
    CosmeticGradingImage,
    CosmeticGradingResult,
} from "services/asset-management/AssetGradingService";
import { Asset, assetService } from "services/asset-management/AssetService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    assetUuid: string;
}

interface GradeInfo {
    titleKey: string;
    detailsKey: string;
}

interface DevicePhotoTile {
    url: string;
    label: string;
}

const GRADE_INFO: Record<string, GradeInfo> = {
    A: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeA.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeA.details",
    },
    B: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeB.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeB.details",
    },
    C: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeC.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeC.details",
    },
    D: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeD.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeD.details",
    },
};

// The cosmetic grading API returns imageType as FRONT/BACK/SIDE_LEFT/SIDE_RIGHT/SIDE_TOP/SIDE_BOTTOM/SIDE, which
// doesn't match the existing DevicePhotos.positions translation keys (front/back/top/bottom/leftSide/rightSide/side).
const IMAGE_TYPE_TO_POSITION_KEY: Record<string, string> = {
    FRONT: "front",
    BACK: "back",
    SIDE_TOP: "top",
    SIDE_BOTTOM: "bottom",
    SIDE_LEFT: "leftSide",
    SIDE_RIGHT: "rightSide",
    SIDE: "side",
};

function toDevicePhotoPositionKey(imageType: string): string {
    return IMAGE_TYPE_TO_POSITION_KEY[imageType] ?? imageType;
}

export const AssetGradingView = (props: Props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [asset, setAsset] = React.useState<Asset>();
    const [error, setError] = React.useState<string>();
    const [cosmeticGradingResult, setCosmeticGradingResult] = React.useState<CosmeticGradingResult>();
    const [lightBoxGalleryIndex, setLightBoxGaleryIndex] = React.useState(-1);

    const toDevicePhotoImages = (image: CosmeticGradingImage): DevicePhotoTile[] => {
        const positionLabel = t(
            `AssetManagement.sidePanel.grading.DevicePhotos.positions.${toDevicePhotoPositionKey(image.imageType)}`
        );

        const tiles: DevicePhotoTile[] = [];
        if (image.presignedImageUrl !== undefined) {
            tiles.push({ url: image.presignedImageUrl, label: positionLabel });
        }

        if (image.debugPresignedImageUrl !== undefined) {
            tiles.push({
                url: image.debugPresignedImageUrl,
                label: `${positionLabel} (${t("AssetManagement.sidePanel.grading.DevicePhotos.variants.debug")})`,
            });
        }

        if (image.privacyPresignedImageUrl !== undefined) {
            tiles.push({
                url: image.privacyPresignedImageUrl,
                label: `${positionLabel} (${t("AssetManagement.sidePanel.grading.DevicePhotos.variants.privacy")})`,
            });
        }

        if (image.damagePresignedImageUrl !== undefined) {
            tiles.push({
                url: image.damagePresignedImageUrl,
                label: `${positionLabel} (${t("AssetManagement.sidePanel.grading.DevicePhotos.variants.damage")})`,
            });
        }

        if (image.heatmapPresignedImageUrl !== undefined) {
            tiles.push({
                url: image.heatmapPresignedImageUrl,
                label: `${positionLabel} (${t("AssetManagement.sidePanel.grading.DevicePhotos.variants.heatmap")})`,
            });
        }
        return tiles;
    };

    useEffect(() => {
        setLoading(true);
        setError(undefined);
        const abortController = new AbortController();
        assetService
            .fetchAssetByUuid(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setAsset(response);
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setError("Failed to load grading data");
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid]);

    useEffect(() => {
        const abortController = new AbortController();
        assetGradingService
            .fetchCosmeticGradingResult(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setCosmeticGradingResult(response);
                }
            })
            .catch((exception) => {
                if (!abortController.signal.aborted) {
                    logger.warn("Failed to fetch cosmetic grading result", exception.message);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid]);

    if (loading) {
        return (
            <Box horizontalAlignment="Center" verticalAlignment="Center" className={style.loadingState}>
                <LogoLoader />
            </Box>
        );
    }

    if (error) {
        return (
            <Box columnLayout gap="Xs">
                <TextBlock disableBottomSpacing>{error}</TextBlock>
            </Box>
        );
    }

    if (asset === undefined) {
        return null;
    }

    const assetRecord = asset as Record<
        string,
        string | string[] | boolean | { key: string; value: string }[] | Asset[] | undefined
    >;
    const blanccoGrade = assetRecord.blancco_grade;
    const gradeKey = typeof blanccoGrade === "string" && blanccoGrade ? blanccoGrade.toUpperCase() : undefined;
    const gradeInfo = gradeKey !== undefined ? GRADE_INFO[gradeKey] : undefined;

    if (gradeKey !== undefined && gradeInfo === undefined) {
        return (
            <Box columnLayout gap="Xs">
                <TextBlock disableBottomSpacing>{t("AssetManagement.sidePanel.grading.invalidGrade")}</TextBlock>
            </Box>
        );
    }

    const testResults = Object.entries(cosmeticGradingResult?.testResults ?? {});
    const devicePhotoTiles = (cosmeticGradingResult?.images ?? []).flatMap((image) => toDevicePhotoImages(image));
    const lightBoxGalleryImages = devicePhotoTiles.map((devicePhotoTile: DevicePhotoTile) => ({
        src: devicePhotoTile.url,
        description: devicePhotoTile.label,
    }));

    const hasCosmeticGradingData = testResults.length > 0 || devicePhotoTiles.length > 0;

    if (gradeInfo === undefined && !hasCosmeticGradingData) {
        return (
            <Box columnLayout gap="Xs">
                <TextBlock disableBottomSpacing>{t("AssetManagement.sidePanel.grading.noGradeAvailable")}</TextBlock>
            </Box>
        );
    }

    return (
        <Box columnLayout gap={"Md"}>
            <Box columnLayout gap="Sm">
                {gradeKey !== undefined && gradeInfo !== undefined && (
                    <>
                        <GradeBadge
                            grade={gradeKey as "A" | "B" | "C" | "D"}
                            size={48}
                            withBackground
                            title={t(gradeInfo.titleKey)}

                            description={t(gradeInfo.detailsKey)}
                        />
                        <Box gap="Sm" verticalAlignment="Top">
                            <span className={style.detailLabel}>
                                {t("AssetManagement.value.gradingTable.actionHeader")}
                            </span>
                            <Box columnLayout gap="Xxs" className={style.gradingActionField}>
                                <GradingActionSelect
                                    key={props.assetUuid}
                                    asset={asset}
                                    testId={testIds.workArea.asset.sidePanel.gradingTab.actionSelect}
                                />
                                <TextBlock disableBottomSpacing className={style.gradingActionHelperText}>
                                    {t("AssetManagement.sidePanel.grading.actionDescription")}
                                </TextBlock>
                            </Box>
                        </Box>
                    </>
                )}
                {gradeInfo === undefined && (
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.noGradeAvailable")}
                    </TextBlock>
                )}
            </Box>
            <Divider direction={"Horizontal"} />
            <Box columnLayout gap={"Xs"}>
                <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                    {t("AssetManagement.sidePanel.grading.AiCosmetic.title")}
                </Heading>
                {testResults.length === 0 ? (
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.AiCosmetic.noIssuesDetected")}
                    </TextBlock>
                ) : (
                    testResults.map(([testName, result]) => (
                        <Box key={testName} gap={"Xxs"} justifyContent={"SpaceBetween"}>
                            <TextBlock disableBottomSpacing className={style.detailLabel}>
                                {t(`DiagnosticReportsTable.diagnosticSummary.tests.${testName}`)}
                            </TextBlock>
                            <TextBlock
                                disableBottomSpacing
                                className={classNames(style.detailValue, style.detailValueRight)}
                            >
                                {t(
                                    result
                                        ? "AssetManagement.sidePanel.grading.AiCosmetic.detected"
                                        : "AssetManagement.sidePanel.grading.AiCosmetic.notDetected"
                                )}
                            </TextBlock>
                        </Box>
                    ))
                )}
            </Box>
            <Divider direction={"Horizontal"} />
            <Box columnLayout gap={"Xs"}>
                <Box justifyContent={"SpaceBetween"}>
                    <Heading disableBottomSpacing disableTopSpacing variant={"SUBTITLE_1"}>
                        {t("AssetManagement.sidePanel.grading.DevicePhotos.title")}
                    </Heading>
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.DevicePhotos.totalImages", {
                            totalImages: devicePhotoTiles.length,
                        })}
                    </TextBlock>
                </Box>
                <TextBlock disableBottomSpacing>
                    {t("AssetManagement.sidePanel.grading.DevicePhotos.description")}
                </TextBlock>
                <div className={style.gradingPhotoWrapper}>
                    {devicePhotoTiles.map((image, index) => (
                        <Box
                            key={index}
                            columnLayout
                            gap={"Xxxs"}
                            verticalAlignment={"Center"}
                            onClick={() => setLightBoxGaleryIndex(index)}
                        >
                            <img src={image.url} alt={image.label} loading="lazy" />
                            <TextBlock disableBottomSpacing>{image.label}</TextBlock>
                        </Box>
                    ))}
                </div>

                <Lightbox
                    index={lightBoxGalleryIndex}
                    plugins={[Zoom, Captions]}
                    slides={lightBoxGalleryImages}
                    open={lightBoxGalleryIndex >= 0}
                    close={() => setLightBoxGaleryIndex(-1)}
                    render={{
                        iconPrev: () => <ArrowLeft size={32} color={"--theme-icon-fill-white"} />,
                        iconNext: () => <ArrowRight size={32} color={"--theme-icon-fill-white"} />,
                        iconClose: () => <Close size={32} color={"--theme-icon-fill-white"} />,
                        iconZoomIn: () => <ZoomIn size={32} color={"--theme-icon-fill-white"} />,
                        iconZoomOut: () => <ZoomOut size={32} color={"--theme-icon-fill-white"} />,
                    }}
                    portal={{
                        // Prevent table side panel from closing itself on pointer down events outside its DOM subtree
                        container: { onPointerDown: (event) => event.stopPropagation() },
                    }}
                    captions={{
                        descriptionTextAlign: "center",
                    }}
                    className={style.lightbox}
                />
            </Box>
        </Box>
    );
};
